import PropTypes from "prop-types";
import React, { useCallback, useRef } from "react";
// @mui
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import TreeView from "src/consuman-components/tree-view";
// hooks
import { useBoolean } from "src/hooks/use-boolean";
// locales
import { useLocales } from "src/locales";
// components
import CustomBreadcrumbs from "src/components/custom-breadcrumbs";
import Submit from "src/consuman-components/generic/submit";
// api
import { useGetPlainTree as useGetPlainTreeCategory } from "src/api/category";
import { CategoryTreeItem } from "src/consuman-components/generic/categories/categories";

const categoryTreeDrawer = React.forwardRef(
  (
    {
      onChange,
      name,
      label,
      type,
      disableClearable = true,
      disableContainer = false,
      disabledId = null,
      multiple = false,
      onlyContainer = false,
      ...props
    },
    ref
  ) => {
    const treeViewRef = useRef(null);

    const { t } = useLocales();

    const drawer = useBoolean(false);
    const {
      tree: dataSource,
      isLoading,
      isValidating,
    } = useGetPlainTreeCategory(drawer.value, onlyContainer, disableContainer);

    const showDrawer = value => {
      if (value) drawer.onTrue();
      else drawer.onFalse();
    };

    React.useImperativeHandle(ref, () => ({
      showDrawer,
    }));

    const handleSelectionChanged = useCallback(
      e => {
        const selectedNodes = treeViewRef.current.instance.getSelectedNodes();
        if (!disableContainer || selectedNodes.find(x => x.itemData.isContainer === false)) {
          if (drawer.value && !multiple) {
            const nodes = selectedNodes.map(n => n.itemData);

            const selected = nodes[0];

            if (nodes.length > 0) drawer.onFalse();

            if (onChange) onChange(selected);
          }
        }
      },
      [multiple, drawer, disableContainer, onChange]
    );

    const itemRender = useCallback(item => <CategoryTreeItem item={item} />, []);

    return (
      <Drawer
        key={name}
        anchor="right"
        open={drawer.value}
        onClose={drawer.onFalse}
        PaperProps={{
          sx: { width: "35%" },
        }}
      >
        <Container sx={{ marginTop: "3%" }}>
          <CustomBreadcrumbs
            heading={t("selectGeneric", { label: label || t("category") })}
            action={
              <Submit
                onClick={drawer.onFalse}
                showSave={false}
                margin="0"
                back
              />
            }
            sx={{ mb: { xs: 3, md: 2 } }}
          />
          {isLoading || isValidating ? (
            <Stack
              sx={{ position: "relative" }}
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress variant="indeterminate" />
            </Stack>
          ) : (
            <TreeView
              ref={treeViewRef}
              id={name}
              key={name}
              dataSource={dataSource.filter(x => x.id !== disabledId)}
              searchEnabled
              disableSelectedDescendants={multiple}
              selectionMode={multiple ? "multiple" : "single"}
              showCheckBoxesMode={multiple ? "normal" : "none"}
              selectByClick
              rootValue={null}
              itemRender={itemRender}
              keyExpr="id"
              parentIdExpr="parentId"
              displayExpr="name"
              disabledExpr="disabledItem"
              expandedExpr="expanded"
              onSelectionChanged={handleSelectionChanged}
            />
          )}
        </Container>
      </Drawer>
    );
  }
);

categoryTreeDrawer.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  disabledId: PropTypes.any,
  disableContainer: PropTypes.any,
  onChange: PropTypes.any,
  onlyContainer: PropTypes.any,
  type: PropTypes.any,
  disableClearable: PropTypes.bool,
  multiple: PropTypes.bool,
};

export default categoryTreeDrawer;
